import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, ExpandMore } from '@mui/icons-material';
import { Chapitre, Exercice, Lecon, Question } from '../../types';
import { useChapitres, useLecons } from '../../hooks/useData';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import type { FieldChangeEvent } from '../../types/forms';
import { logger } from '../../lib/logger';

interface ExerciceFormProps {
  exercice?: Exercice | null;
  defaultLeconId?: string;
  defaultChapitreId?: string;
  returnPath?: string;
  saving?: boolean;
  /** Mode lecture seule - désactive tous les champs et boutons */
  readOnly?: boolean;
  onSubmit: (data: Omit<Exercice, 'id'>) => void;
  onCancel: () => void;
  onChange?: (data: Omit<Exercice, 'id'>) => void;
  /** Callback appelé quand un champ perd le focus (pour auto-save) */
  onFieldBlur?: (field: string, value: unknown, allFormData: Omit<Exercice, 'id'>) => void;
  /** Chapitres proposés : fournis avec `lecons`, le formulaire ne charge plus les collections entières. */
  chapitres?: Pick<Chapitre, 'id' | 'titre'>[];
  /** Leçons proposées (voir `chapitres`). */
  lecons?: Pick<Lecon, 'id' | 'titre' | 'chapitreId'>[];
}

const ExerciceForm: React.FC<ExerciceFormProps> = ({ exercice, defaultLeconId, defaultChapitreId, returnPath, saving, readOnly, onSubmit, onCancel, onChange, onFieldBlur, chapitres: chapitresFournis, lecons: leconsFournies }) => {
  const { environment } = useEnvironment();


  const [formData, setFormData] = useState<Omit<Exercice, 'id'>>({
    chapitreId: defaultChapitreId || '',
    leconId: defaultLeconId || '',
    dateCreation: new Date().toISOString().split('T')[0],
    dateModification: new Date().toISOString().split('T')[0],
    titre: '',
    description: '',
    niveau: 'moyen',
    questions: [],
    competencesVisees: [],
    tempsEstimeMinutes: 30,
    estActif: true,
    statut: 'draft' // Par défaut en brouillon
  });

  const [newCompetence, setNewCompetence] = useState('');

  // React Query hooks
  const listesFournies = !!chapitresFournis && !!leconsFournies;
  const { data: chapitresCharges = [] } = useChapitres(environment, { enabled: !listesFournies });
  const { data: leconsChargees = [] } = useLecons(environment, { enabled: !listesFournies });
  const chapitres = chapitresFournis ?? chapitresCharges;
  const lecons = leconsFournies ?? leconsChargees;

  // Filtrer les leçons par chapitre
  const filteredLecons = useMemo(() => {
    if (!formData.chapitreId) return [];
    return lecons.filter(lecon => lecon.chapitreId === formData.chapitreId);
  }, [formData.chapitreId, lecons]);

  // Reset leconId si pas valide pour le chapitre sélectionné
  useEffect(() => {
    if (formData.chapitreId && lecons.length > 0 && formData.leconId) {
      const isValid = filteredLecons.find(l => l.id === formData.leconId);
      if (!isValid) {
        setFormData(prev => ({ ...prev, leconId: '' }));
      }
    } else if (!formData.chapitreId && lecons.length > 0) {
      setFormData(prev => ({ ...prev, leconId: '' }));
    }
    // formData.leconId est volontairement exclu : l'effet le remet à zéro,
    // il se relancerait sur sa propre écriture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.chapitreId, lecons, filteredLecons]);

  useEffect(() => {
    if (exercice) {
      // Données de base de l'exercice
      const baseData: Omit<Exercice, 'id'> = {
        chapitreId: exercice.chapitreId,
        leconId: exercice.leconId,
        dateCreation: exercice.dateCreation,
        dateModification: new Date().toISOString().split('T')[0],
        titre: exercice.titre,
        description: exercice.description,
        niveau: exercice.niveau,
        questions: exercice.questions,
        competencesVisees: exercice.competencesVisees,
        tempsEstimeMinutes: exercice.tempsEstimeMinutes || 30,
        estActif: exercice.estActif !== undefined ? exercice.estActif : true,
        statut: exercice.statut || 'draft',
        datePublication: exercice.datePublication,
        historiqueStatut: exercice.historiqueStatut
      };


      setFormData(baseData);
    }
  }, [exercice]);

  // Appeler onChange à chaque modification du formData pour la prévisualisation en temps réel
  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData, onChange]);

  const handleChange = (field: keyof Omit<Exercice, 'id'>) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox'
      ? (event.target as HTMLInputElement).checked
      : event.target.value;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSelectChange = (field: keyof Omit<Exercice, 'id'>) => (
    event: FieldChangeEvent
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleAddCompetence = () => {
    if (newCompetence.trim() && !formData.competencesVisees.includes(newCompetence.trim())) {
      setFormData(prev => ({
        ...prev,
        competencesVisees: [...prev.competencesVisees, newCompetence.trim()]
      }));
      setNewCompetence('');
    }
  };

  const handleDeleteCompetence = (competence: string) => {
    setFormData(prev => ({
      ...prev,
      competencesVisees: prev.competencesVisees.filter(c => c !== competence)
    }));
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: `question_${Date.now()}`,
      texte: '',
      type: 'choix_multiple',
      reponseCorrecte: '',
      explication: '',
      options: ['', '', '', ''],
      points: 1,
      illustrationLatex: '',
      lienLatex: ''
    };

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const updateQuestion = (index: number, updatedQuestion: Question) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === index ? updatedQuestion : q)
    }));
  };

  const deleteQuestion = (index: number) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Informations de base */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Informations de base</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Titre"
                  value={formData.titre}
                  onChange={handleChange('titre')}
                  onBlur={() => onFieldBlur?.('titre', formData.titre, formData)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required>
                  <InputLabel>Chapitre</InputLabel>
                  <Select
                    value={formData.chapitreId}
                    onChange={handleSelectChange('chapitreId')}
                    onBlur={() => onFieldBlur?.('chapitreId', formData.chapitreId, formData)}
                  >
                    {chapitres.map((chapitre) => (
                      <MenuItem key={chapitre.id} value={chapitre.id}>
                        {chapitre.titre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth required>
                  <InputLabel>Leçon</InputLabel>
                  <Select
                    value={formData.leconId}
                    onChange={handleSelectChange('leconId')}
                    onBlur={() => onFieldBlur?.('leconId', formData.leconId, formData)}
                    disabled={!formData.chapitreId}
                  >
                    {filteredLecons.map((lecon) => (
                      <MenuItem key={lecon.id} value={lecon.id}>
                        {lecon.titre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                  onBlur={() => onFieldBlur?.('description', formData.description, formData)}
                  multiline
                  minRows={5}
                  maxRows={12}
                  required
                  placeholder="Décrivez l'exercice en détail..."
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Niveau</InputLabel>
                  <Select
                    value={formData.niveau}
                    onChange={handleSelectChange('niveau')}
                    onBlur={() => onFieldBlur?.('niveau', formData.niveau, formData)}
                    required
                  >
                    <MenuItem value="facile">Facile</MenuItem>
                    <MenuItem value="moyen">Moyen</MenuItem>
                    <MenuItem value="difficile">Difficile</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  label="Durée estimée (minutes)"
                  type="number"
                  value={formData.tempsEstimeMinutes}
                  onChange={handleChange('tempsEstimeMinutes')}
                  onBlur={() => onFieldBlur?.('tempsEstimeMinutes', formData.tempsEstimeMinutes, formData)}
                />
              </Grid>
              {/* Contrôles de statut */}
              <>
                <Grid item xs={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={formData.statut || 'draft'}
                      onChange={handleSelectChange('statut')}
                      required
                    >
                      <MenuItem value="draft">🔸 Brouillon</MenuItem>
                      <MenuItem value="published">✅ Publié</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.estActif}
                        onChange={(e) => setFormData(prev => ({ ...prev, estActif: e.target.checked }))}
                      />
                    }
                    label="Actif"
                  />
                </Grid>
              </>
            </Grid>
          </Paper>
        </Grid>

        {/* Compétences visées */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Compétences visées</Typography>
            <Box display="flex" gap={1} mb={2}>
              <TextField
                label="Nouvelle compétence"
                value={newCompetence}
                onChange={(e) => setNewCompetence(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddCompetence();
                  }
                }}
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <Button onClick={handleAddCompetence} variant="outlined">
                Ajouter
              </Button>
            </Box>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {formData.competencesVisees.map((competence, index) => (
                <Chip
                  key={index}
                  label={competence}
                  onDelete={() => handleDeleteCompetence(competence)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Questions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Questions ({formData.questions.length})</Typography>
              <Button
                startIcon={<Add />}
                onClick={addQuestion}
                variant="outlined"
              >
                Ajouter une question
              </Button>
            </Box>

            {formData.questions.map((question, index) => (
              <Accordion key={question.id} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography>
                      Question {index + 1}: {question.texte.substring(0, 50)}...
                    </Typography>
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteQuestion(index);
                      }}
                      size="small"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <QuestionForm
                    question={question}
                    onChange={(updatedQuestion) => updateQuestion(index, updatedQuestion)}
                    returnPath={returnPath}
                    onQuestionBlur={() => onFieldBlur?.('questions', formData.questions, formData)}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} disabled={saving}>
          {readOnly ? 'Fermer' : 'Annuler'}
        </Button>
        {!readOnly && (
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {saving ? 'Enregistrement...' : (exercice ? 'Mettre à jour' : 'Créer')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

// Composant pour éditer une question
interface QuestionFormProps {
  question: Question;
  onChange: (question: Question) => void;
  returnPath?: string;
  /** Callback appelé quand un champ de la question perd le focus */
  onQuestionBlur?: () => void;
}

const QuestionForm: React.FC<QuestionFormProps> = ({ question, onChange, onQuestionBlur }) => {
  const handleChange = (field: keyof Question) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChange({
      ...question,
      [field]: event.target.value
    });
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...(question.options || [])];
    newOptions[index] = value;
    onChange({
      ...question,
      options: newOptions
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Texte de la question"
          value={question.texte}
          onChange={handleChange('texte')}
          onBlur={onQuestionBlur}
          multiline
          minRows={3}
          maxRows={8}
          required
          placeholder="Entrez le texte de la question..."
        />
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel>Type de question</InputLabel>
          <Select
            value={question.type}
            onChange={(e) => onChange({ ...question, type: e.target.value as Question['type'] })}
            onBlur={onQuestionBlur}
          >
            <MenuItem value="choix_multiple">Choix multiple</MenuItem>
            <MenuItem value="vrai_faux">Vrai/Faux</MenuItem>
            <MenuItem value="texte_libre">Texte libre</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Points"
          type="number"
          value={question.points}
          onChange={handleChange('points')}
          onBlur={onQuestionBlur}
        />
      </Grid>
      <Grid item xs={3}>
        <TextField
          fullWidth
          label="Réponse correcte"
          value={question.reponseCorrecte}
          onChange={handleChange('reponseCorrecte')}
          onBlur={onQuestionBlur}
        />
      </Grid>

      {question.type === 'choix_multiple' && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Options</Typography>
          <Grid container spacing={1}>
            {(question.options || []).map((option, index) => (
              <Grid item xs={6} key={index}>
                <TextField
                  fullWidth
                  label={`Option ${String.fromCharCode(65 + index)}`}
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  onBlur={onQuestionBlur}
                  size="small"
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Explication de la réponse"
          value={question.explication}
          onChange={handleChange('explication')}
          onBlur={onQuestionBlur}
          multiline
          minRows={4}
          maxRows={10}
          placeholder="Expliquez pourquoi cette réponse est correcte..."
          helperText="Cette explication sera affichée à l'élève après sa réponse"
        />
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
            Illustration LaTeX
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Code LaTeX complet"
          value={question.illustrationLatex || ''}
          onChange={handleChange('illustrationLatex')}
          multiline
          rows={4}
          placeholder="Code LaTeX de l'illustration (produit hors plateforme)"
          sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
          helperText="Code LaTeX complet pour l'illustration de la question"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Lien Image LaTeX"
          value={question.lienLatex || ''}
          onChange={handleChange('lienLatex')}
          placeholder="URL de l'image d'illustration"
          helperText="Image d'illustration poussée avec le contenu (lecture seule)"
          InputProps={{
            readOnly: true,
          }}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              color: 'text.secondary'
            }
          }}
        />
        {question.lienLatex && (
          <Box sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Prévisualisation de l'image générée:
            </Typography>
            <Box
              component="img"
              src={question.lienLatex}
              alt="Illustration LaTeX"
              sx={{
                maxWidth: '100%',
                maxHeight: 300,
                display: 'block',
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
              onError={(event) => {
                logger.error('Failed to load image:', question.lienLatex);
                (event.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </Box>
        )}
      </Grid>

      {question.latexillustration && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Illustration LaTeX (ancien champ)"
            value={question.latexillustration}
            onChange={handleChange('latexillustration')}
            multiline
            rows={3}
            sx={{ fontFamily: 'monospace' }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ExerciceForm;